import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ConsentTypes } from '@credit-acceptance/shareable-utilities';
import { FullStory } from '@fullstory/browser';
import { adobeAnalytics } from 'adobe-analytics';
import { catchError, map, take, takeUntil, tap } from 'rxjs';

import { PREQUAL_JSON } from '../../assets/constants';
import { FeatureFlagService } from '../feature-flag.service';
import { ConsentTextInfoRestService } from '../services/consent-test-info-rest.service';
import { PrequalService } from '../services/prequal.service';
import { ScrollingService } from '../services/scrolling.service';
import { SubmitCapsLeadRestService } from '../services/submit-caps-lead-rest.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { FS_EVENTS, FS_LABELS } from '../shared/constants/fsevents';
import { DealerLocation } from '../shared/models/prequal.types';
import {
  accessToken,
  cacChubSSNId,
  monthlyPayment,
  portalApplicationId,
} from '../shared/state/local-cache';
import { getSelectedDealerCountFromUrl } from '../shared/utils/selected.dealers';

declare const FS: typeof FullStory;

@Component({
  selector: 'capq-openloan',
  templateUrl: './openloan.component.html',
  styleUrls: ['./openloan.component.scss'],
  standalone: false,
})
export class OpenLoanComponent
  extends Unsubscribe
  implements OnInit, AfterViewInit
{
  @Input() immediateEmit = false;
  @Input() isPrequal = false;
  @Input() adobeScriptLoaded = false;
  @Input() submissionFailureReason = '';
  @Input() smsConsentGiven = false;
  @Output() showFinalSuccessDealerWebsite = new EventEmitter<boolean>();

  @ViewChild('topElement') topElement!: ElementRef<HTMLDivElement>;

  onImageLoad() {
    this.isImageLoaded = true;
  }

  isImageLoaded = false;

  selectedDealerCount = 0;

  zipCode = 48034;
  models: string[] = [];
  monthlyPayment = 0;
  logoURL = 'assets/images/CA_Logo.svg';
  prequalImg = 'assets/images/pre-qual.png';
  dealers!: DealerLocation[];
  osmSSNConsentContent = '';
  isUserAgreed = false;
  desktopView: boolean | undefined;
  finalResponse = false;
  saveMeCounter = 60;
  formFieldData: any;
  consentCreditAuthVersion: any;
  consentversionid = '';
  globalText: any;
  timeIntervalId: any;
  ttMETAstarttime: number | null = null;
  isWaiting = false;
  residenceData: any = {};
  userSelectedDealersCount = 0;

  constructor(
    private prequalService: PrequalService,
    private readonly scrollingService: ScrollingService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly submitcapsLeadRestService: SubmitCapsLeadRestService,
    private readonly consentTextInfoRestService: ConsentTextInfoRestService
  ) {
    super();
    this.monthlyPayment = monthlyPayment();
  }

  ngOnInit(): void {
    this.ttMETAstarttime = new Date().getTime();
    this.formFieldData = PREQUAL_JSON?.openloan;

    this.dealers = this.prequalService.getDealerLocations() ?? [];

    // Set userSelectedDealersCount based on the isDealerSelected value to true
    this.userSelectedDealersCount = this.dealers.filter(
      (dealer) => dealer.isDealerSelected
    ).length;

    this.residenceData = this.prequalService.getResidenceData() ?? {};

    this.consentTextInfoRestService
      .getConsentByType(ConsentTypes.OSM_SSN)
      .pipe(
        tap((resp) => {
          const accessTokenRef = resp.headers?.get('access-token');
          if (accessTokenRef) accessToken(accessTokenRef);

          const chubSSNid = resp.headers?.get('cac-chub-ssid');
          if (chubSSNid) cacChubSSNId(chubSSNid);
        }),
        map((result) => result.body?.getConsentByType),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((data) => {
        if (data) {
          this.osmSSNConsentContent = data.consenttext;
          this.consentCreditAuthVersion = data.consenttextid;
          this.consentversionid = data.consentversion;
        }
      });

    this.featureFlagService
      .isEnabled('enabled-select-dealers-prequalification')
      .pipe(
        take(1),
        tap((isEnabled) => {
          this.selectedDealerCount = isEnabled
            ? getSelectedDealerCountFromUrl()
            : 0;
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    setTimeout(() => {
      this.startTimer();
    }, 180000);

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-page-view', null, {
        form_design: 'v 1.0',
        section_name: 'prequalified',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list: '',
        form_submission_status: 'failure',
        form_failure_reason: this.submissionFailureReason
          ? this.submissionFailureReason
          : undefined,
        event_type: 'cac-page-view',
      });
  }

  ngAfterViewInit(): void {
    this.scrollingService.scrollToTop(this.topElement.nativeElement);
  }

  startTimer() {
    this.timeIntervalId = setInterval(() => {
      this.saveMeCounter = this.saveMeCounter - 1;
      if (this.saveMeCounter == 0) {
        clearInterval(this.saveMeCounter);
        document.location.href = document.location.origin;
      }
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.timeIntervalId);
  }

  handleSubmit() {
    this.isWaiting = true;
    let dealer_list = '';
    this.dealers.forEach((dealer, ind) => {
      dealer_list += `${dealer.dealerId}|${dealer.dealerName}`;
      if (ind < 2) {
        dealer_list += ', ';
      }
    });
    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-save-me-time', null, {
        form_design: 'v 1.0',
        section_name: '',
        form_submission_id: (portalApplicationId() ?? '').toString(),
        dealer_list,
        form_submission_status: 'success',
        event_type: 'cac-save-me-time',
      });

    this.submitcapsLeadRestService
      .submitCapsLead({
        agreementConsent: this.isUserAgreed,
        ...(this.smsConsentGiven && { smsConsent: true }),
      })
      .pipe(
        map(() => {
          this.isWaiting = false;
        }),
        catchError(() => {
          this.prequalService.triggerErrorScreen(true);
          throw new Error();
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();

    if (!(typeof FS === 'undefined')) {
      FS.event(FS_EVENTS.OSM_HOT_LEAD, {
        action_label: FS_LABELS.SAVE_ME_TIME,
        osmHotLeadSubmitted: true,
      });
    }

    this.finalResponse = true;
    if (this.dealers?.length) {
      this.showFinalSuccessDealerWebsite.emit(true);
    }
    this.stopTimer();
  }

  getDealerFinderUrl(): string {
    const baseUrl = '/car-buyers/dealer-finder';
    const city = this.residenceData?.city;
    const state = this.residenceData?.userState;
    if (state && city) {
      return `${baseUrl}/${state}-${city}`;
    }
    return baseUrl;
  }

  onCheckboxChange(e: any) {
    if (e.target.checked) {
      this.isUserAgreed = true;
    } else {
      this.isUserAgreed = false;
    }
  }
}
